import ScopeChangeRequest, {
  ScopeChangeRequestItem,
} from 'admin/scope-change-requests/ScopeChangeRequest'
import ScopeChangeRequestStore from 'admin/ScopeChangeRequestStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import { action, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { validateElements } from 'utils/validation-utils'
import { v4 } from 'uuid'
import { required, validationContext } from 'validx'

export default class ScopeChangeEditCustomItemsDialogViewModel extends ModalViewModel {
  @observable
  scopeChangeRequest = ScopeChangeRequest.none

  @observable
  editScopeChangeCustomItems: EditScopeChangeCustomItemViewModel[] = []

  readonly scopeChangesValidationStep = validationContext(this, {
    editScopeChangeCustomItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  constructor(
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private onSave: () => void,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Opens the dialog
   * @param scopeChangeRequest
   */
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.editScopeChangeCustomItems = scopeChangeRequest.customItems.map(
      EditScopeChangeCustomItemViewModel.fromCustomScopeChangeItem,
    )
    this.show()
  }

  /**
   * Edit the scope change request and close the dialog
   */
  save = task.resolved(async () => {
    if (!this.scopeChangesValidationStep.validate().isValid) {
      return
    }

    await this.scopeChangeRequestStore
      .editScopeChangeRequest(
        this.scopeChangeRequest.id,
        this.editScopeChangeCustomItems.map((i) => ({
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          proPrice: i.proPrice,
        })),
        this.scopeChangeRequest.inventoryItems.map((i) => ({
          versionId: i.inventoryItemVersionId,
          quantity: i.quantity,
        })),
      )
      .then(async () => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Scope change items have been updated')
          .autoDismiss(2000)
          .show()

        await this.scopeChangeRequestStore.fetchScopeChangeRequest(
          this.scopeChangeRequest.id,
        )

        this.onSave()
        this.close()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', e.message)
          .autoDismiss(2000)
          .show()
      })
  })

  /**
   * Adds a new custom scope change
   */
  @action.bound
  addScopeChange() {
    this.editScopeChangeCustomItems.push(
      new EditScopeChangeCustomItemViewModel('', 1, 0, 0),
    )
  }

  /**
   * Removes a previously added custom scope change.
   *
   * @param key
   */
  @action.bound
  removeScopeChange(key: string) {
    const pos = this.editScopeChangeCustomItems.findIndex(
      (item) => item.key === key,
    )
    this.editScopeChangeCustomItems.splice(pos, 1)
  }
}

/**
 * View Model for a editing a scope change item.
 */
export class EditScopeChangeCustomItemViewModel {
  @observable key = ''

  @observable name = ''

  @observable quantity = 1

  @observable price = 0

  @observable proPrice = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    name: [required('Service name is required.')],
    quantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
    price: [(opts) => opts.value >= 0 || 'Price cannot be negative'],
    proPrice: [(opts) => opts.value >= 0 || 'Pro price cannot be negative'],
  })

  constructor(name: string, quantity: number, price: number, proPrice: number) {
    makeObservable(this)
    this.key = v4()
    this.name = name
    this.quantity = quantity
    this.price = price
    this.proPrice = proPrice
  }

  @action.bound
  incrementQuantity() {
    this.quantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.quantity === 1) {
      return
    }

    this.quantity -= 1
  }

  @action.bound
  setQuantity(value: string) {
    this.quantity = parseInt(value, 10) || 1
  }

  @action.bound
  setName(value: string) {
    this.name = value
  }

  static fromCustomScopeChangeItem(item: ScopeChangeRequestItem) {
    return new EditScopeChangeCustomItemViewModel(
      item.name,
      item.quantity,
      item.price,
      item.proPrice,
    )
  }
}
